import { GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import {
  AutoCompleteUncontrolled,
  DatePickerUncontrolled,
  DateRangeSelect,
  ISelectType,
  ModalFilters,
  OverflowTooltip,
  Table,
} from "@4uhub/lib4uhub";
import { useCallback, useState } from "react";
import { Box, Typography } from "@mui/material";

import MainApiService, { MainApi } from "../../services/mainApi.service";
import { IHealthChecksLogs } from "../../models/health-checks-logs";
import useDate from "../../hooks/useDate";

const BANK_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/HealthCheckEvent";
const bankService = new MainApiService<IHealthChecksLogs>(BANK_ROUTE);

const HEALTH_CHECKS_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/HealthCheck/";
const healthCheckService = new MainApi<ISelectType>(HEALTH_CHECKS_ROUTE);

const translationPath = "page.logs.healthChecks.";

const HealthChecksLogsPage: React.FC = () => {
  const { t } = useTranslation();
  const { localeFormat } = useDate();

  // opções de intervalo de data
  const dateOptions: ISelectType[] = [
    {
      id: "today",
      name: t("Lib4uhub.components.date_range.today") || "Hoje",
      value: "today",
    },
    {
      id: "this_week",
      name: t("Lib4uhub.components.date_range.this_week") || "Esta semana",
      value: "this_week",
    },
    {
      id: "this_month",
      name: t("Lib4uhub.components.date_range.this_month") || "Este mês",
      value: "this_month",
    },
    {
      id: "last_month",
      name: t("Lib4uhub.components.date_range.last_month") || "Mês anterior",
      value: "last_month",
    },
    {
      id: "last_30_days",
      name:
        t("Lib4uhub.components.date_range.last_30_days") || "Últimos 30 dias",
      value: "last_30_days",
    },
    {
      id: "last_6_months",
      name:
        t("Lib4uhub.components.date_range.last_6_months") || "Últimos 6 meses",
      value: "last_6_months",
    },
    {
      id: "select_period",
      name:
        t("Lib4uhub.components.date_range.select_period") ||
        "Selecionar período",
      value: "select_period",
    },
  ];
  const defaultDateOption = dateOptions.find((o) => o.value === "today")!;

  // agora permitimos null para o estado, assim setDateRangeOption aceita ISelectType | null
  const [dateRangeOption, setDateRangeOption] = useState<ISelectType | null>(
    defaultDateOption
  );

  const [selectedHealthCheck, setSelectedHealthCheck] =
    useState<ISelectType | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [enableFields, setEnableFields] = useState<boolean>(true);
  const [filterParams, setFilterParams] = useState<
    Record<string, any> | undefined
  >();
  const [resetDateRange, setResetDateRange] = useState<boolean>(false);

  const columns: GridColDef<IHealthChecksLogs>[] = [
    {
      field: "healthCheck",
      headerName: t(translationPath + "name") || "Name",
      flex: 1,
      maxWidth: 350,
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
    {
      field: "data",
      headerName: t(translationPath + "data") || "Data",
      flex: 1,
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Created",
      flex: 1,
      maxWidth: 150,
      renderCell: (params) =>
        params.value ? localeFormat(new Date(params.value), "Pp") : "-",
    },
  ];

  const filterHandler = useCallback(() => {
    // se só um dos dois estiver preenchido, limpa os dois
    if ((startDate && !endDate) || (!startDate && endDate)) {
      setStartDate(null);
      setEndDate(null);
    }

    setFilterParams({
      HealthCheckId: selectedHealthCheck?.id,
      DateInitial:
        startDate && endDate
          ? startDate.toISOString().substring(0, 10)
          : undefined,
      DateEnd:
        endDate && startDate
          ? endDate.toISOString().substring(0, 10)
          : undefined,
    });
  }, [selectedHealthCheck, startDate, endDate]);

  const cleanFilterHandler = useCallback(() => {
    setFilterParams(undefined);
    setSelectedHealthCheck(null);
    setStartDate(null);
    setEndDate(null);
    setResetDateRange((prev) => !prev);
    setDateRangeOption(defaultDateOption);
  }, [defaultDateOption]);

  const hasFilter = Boolean(selectedHealthCheck || (startDate && endDate));

  return (
    <Table<IHealthChecksLogs>
      showDefaultMacroFilters={false}
      service={bankService}
      columns={columns}
      title={t(translationPath + "health_checks")}
      searchInput
      showButton={false}
      showDelete={false}
      showDeleteButton={false}
      serviceProps={filterParams}
      defaultActions={false}
      macroFilters={() => (
        <ModalFilters
          onFilter={filterHandler}
          actionsDisabled={!hasFilter}
          badgeCount={
            filterParams
              ? Object.keys(filterParams).reduce((count, key) => {
                  return filterParams[key] !== undefined ? count + 1 : count;
                }, 0) -
                (filterParams["DateInitial"] && filterParams["DateEnd"] ? 1 : 0)
              : 0
          }
          onCleanFilter={cleanFilterHandler}
        >
          <>
            <AutoCompleteUncontrolled
              label={t(translationPath + "health_checks") || "Health Checks"}
              service={healthCheckService.list}
              onValueSelected={setSelectedHealthCheck}
              value={selectedHealthCheck}
              size="small"
              fullWidth
            />

            <DateRangeSelect
              setEndDate={setEndDate}
              setStartDate={setStartDate}
              setEnableFields={setEnableFields}
              resetToToday={resetDateRange}
              value={dateRangeOption}
              onValueSelected={(opt: ISelectType | null) =>
                setDateRangeOption(opt)
              }
            />

            <Box display="flex" alignItems="center" gap={1}>
              <DatePickerUncontrolled
                value={startDate}
                onChange={setStartDate}
                label={`${t(translationPath + "start_date")}`}
                isClearable={false}
                disable={enableFields}
              />
              <Typography>-</Typography>
              <DatePickerUncontrolled
                value={endDate}
                onChange={setEndDate}
                label={`${t(translationPath + "end_date")}`}
                isClearable={false}
                disable={enableFields}
              />
            </Box>
          </>
        </ModalFilters>
      )}
      searchInputPlaceHolder={
        t(translationPath + "search") || "Search for health checks log"
      }
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default HealthChecksLogsPage;
